<template>
  <Modal
    v-model:visible="isVisible"
    :header="t('rateShop.postToLoadboard')"
    class="post-to-loadboard-modal"
    @keypress.esc="onCancel"
  >
    <form
      id="post-to-loadboard"
      :aria-label="t('rateShop.postToLoadboard')"
      class="post-to-loadboard-modal-form"
    >
      <InputWrapper
        v-if="shouldShowDat"
        input-id="dat"
        :label="t('enumMap.LoadboardSource.DAT')"
        class="loadboard-option"
      >
        <template #label="{ inputId, label }">
          <label :for="inputId">
            <span class="visually-hidden">{{ label }}</span>
            <img :src="datImg" alt="" class="dat-logo" />
          </label>
        </template>

        <template #default="{ inputId }">
          <Checkbox
            :input-id="inputId"
            :model-value="isDatSelected"
            binary
            @update:model-value="toggleSource(LoadboardSource.DAT)"
          />
        </template>
      </InputWrapper>

      <InputWrapper
        v-if="shouldShowTruckstop"
        input-id="truckstop"
        :label="t('enumMap.LoadboardSource.TRUCKSTOP')"
        class="loadboard-option"
      >
        <template #label="{ inputId, label }">
          <label :for="inputId">
            <span class="visually-hidden">{{ label }}</span>
            <img :src="truckstopImg" alt="" class="truckstop-logo" />
          </label>
        </template>

        <template #default="{ inputId }">
          <Checkbox
            :input-id="inputId"
            :model-value="isTruckstopSelected"
            binary
            @update:model-value="toggleSource(LoadboardSource.TRUCKSTOP)"
          />
        </template>
      </InputWrapper>

      <InputWrapper
        v-slot="{ inputId, inputClasses, invalid }"
        :input-id="`${uid}-rate`"
        :label="t('rateShop.rates')"
        :errors="v$.rate.$errors"
      >
        <InputNumber
          :id="inputId"
          v-model="loadboardData.rate"
          :min="0"
          :currency="Currency.USD"
          :input-class="inputClasses || undefined"
          :invalid="invalid"
          use-grouping
          mode="currency"
          :placeholder="t('rateShop.rates')"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputId, inputClasses, invalid }"
        :input-id="`${uid}-expiration-date`"
        :label="t('common.expiration')"
        :errors="v$.expirationDate.$errors"
      >
        <RygenCalendar
          :id="inputId"
          v-model="loadboardData.expirationDate"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          date-format="mm/dd/yy"
          show-icon
          :placeholder="t('common.expiration')"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputId, inputClasses, invalid }"
        :input-id="`${uid}-mode`"
        :label="t('shipments.modes')"
        :errors="v$.requestedMode.$errors"
      >
        <RygenDropdown
          :id="inputId"
          v-model="loadboardData.requestedMode"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :options="availableModes"
          :placeholder="t('shipments.modes')"
          option-label="label"
          option-value="value"
          append-to="body"
          filter
          show-clear
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputId, inputClasses, invalid }"
        :input-id="`${uid}-equipment-type`"
        :label="t('common.paramTypes', { param: t('referenceData.equipment') })"
        :errors="v$.equipmentType.$errors"
      >
        <RygenDropdown
          :id="inputId"
          v-model="loadboardData.equipmentType"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :loading="equipmentTypes.loading"
          :options="equipmentTypes.data"
          :placeholder="
            t('common.paramTypes', { param: t('referenceData.equipment') })
          "
          option-label="name"
          data-key="id"
          append-to="body"
          filter
          show-clear
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ inputId }"
        :input-id="`${uid}-comment`"
        :label="t('common.comments', 2)"
        optional
      >
        <Textarea
          :id="inputId"
          v-model="loadboardData.comment"
          auto-resize
          :placeholder="t('common.comments', 2)"
        />
      </InputWrapper>
    </form>

    <template #footer>
      <RygenButton
        :label="t('common.cancel')"
        mode="tertiary"
        @click="onCancel"
      />
      <RygenButton
        :label="t('common.post')"
        mode="primary"
        icon="pi pi-send"
        :loading="loading"
        :disabled="selectedSources.length === 0"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { computed, onMounted, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';
import { Currency, EquipmentType, Mode } from '@rygen/rygen-ui/model';

import datImg from '@/assets/img/dat_logo.png';
import truckstopImg from '@/assets/img/truckstop_logo.jpg';
import { LoadboardSource, PostToLoadboardCommand } from '@/model';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';
import { useLoadboardIntegrationStore } from '@/stores/LoadboardIntegration';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Checkbox from 'primevue/checkbox';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  equipmentType: {
    type: Object as PropType<EquipmentType>,
    default: undefined,
  },
  activeLoadboards: {
    type: Array as PropType<LoadboardSource[]>,
    default: () => [],
  },
  requestedMode: {
    type: String as PropType<Mode>,
    default: undefined,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { equipmentType, activeLoadboards, requestedMode, loading } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'confirm', command: PostToLoadboardCommand): void;
}>();

const isDatEnabled = useFlag('loadboard-dat-feat-flag');
const isTruckstopEnabled = useFlag('loadboard-truckstop-feat-flag');

const { equipmentTypes, loadEquipmentTypes } = useEquipmentTypeStore();
const { t } = useI18n();
const { hasConfiguredSource, loadConfiguredLoadboards } =
  useLoadboardIntegrationStore();
const { uid } = useUid('post-to-loadboard-modal');

const availableModes = mapEnumToTranslatedDropdownValues(Mode, 'Mode');

const selectedSources = ref<LoadboardSource[]>([]);
const loadboardData = ref({
  rate: undefined as number | undefined,
  expirationDate: undefined as Date | undefined,
  comment: '',
  requestedMode: requestedMode?.value,
  equipmentType: equipmentType?.value,
});

const rules = computed(() => ({
  rate: {
    required: required(t('rateShop.rates')),
  },
  expirationDate: {
    required: required(t('common.expiration')),
  },
  requestedMode: {
    required: required(t('shipments.modes')),
  },
  equipmentType: {
    required: required(
      t('common.paramTypes', { param: t('referenceData.equipment') }),
    ),
  },
}));
const { v$, validate } = useValidations(loadboardData, rules, uid);

const shouldShowDat = computed(
  () =>
    isDatEnabled.value &&
    hasConfiguredSource(LoadboardSource.DAT) &&
    !activeLoadboards.value.includes(LoadboardSource.DAT),
);

const shouldShowTruckstop = computed(
  () =>
    isTruckstopEnabled.value &&
    hasConfiguredSource(LoadboardSource.TRUCKSTOP) &&
    !activeLoadboards.value.includes(LoadboardSource.TRUCKSTOP),
);

const isDatSelected = computed(() =>
  selectedSources.value.includes(LoadboardSource.DAT),
);

const isTruckstopSelected = computed(() =>
  selectedSources.value.includes(LoadboardSource.TRUCKSTOP),
);

const toggleSource = (source: LoadboardSource) => {
  selectedSources.value = selectedSources.value.includes(source)
    ? selectedSources.value.filter((s) => s !== source)
    : [...selectedSources.value, source];
};

const resetForm = () => {
  selectedSources.value = [];
  loadboardData.value = {
    rate: undefined,
    expirationDate: undefined,
    comment: '',
    requestedMode: requestedMode?.value,
    equipmentType: equipmentType?.value,
  };
};

const onCancel = () => {
  isVisible.value = false;
};

const onConfirm = async () => {
  if (!(await validate())) {
    return;
  }

  const command: PostToLoadboardCommand = {
    loadboards: selectedSources.value,
    requestedRate: loadboardData.value.rate,
    expirationDate: loadboardData.value.expirationDate,
    requestedMode: loadboardData.value.requestedMode,
    equipmentTypeNaturalId: loadboardData.value.equipmentType?.naturalId,
    comments: loadboardData.value.comment,
  };

  emit('confirm', command);
};

watch(isVisible, (newVal: boolean) => {
  if (newVal) {
    resetForm();
  }
  v$.value.$reset();
});

watch(shouldShowDat, (newVal: boolean) => {
  if (!newVal) {
    selectedSources.value = selectedSources.value.filter(
      (s) => s !== LoadboardSource.DAT,
    );
  }
});

watch(shouldShowTruckstop, (newVal: boolean) => {
  if (!newVal) {
    selectedSources.value = selectedSources.value.filter(
      (s) => s !== LoadboardSource.TRUCKSTOP,
    );
  }
});

onMounted(() => {
  loadEquipmentTypes();
  loadConfiguredLoadboards();
});
</script>

<style>
.post-to-loadboard-modal {
  .post-to-loadboard-modal-form {
    display: flex;
    flex-flow: column;
    gap: var(--gap-1);

    .loadboard-option {
      flex-flow: row-reverse;
      align-items: center;
      justify-content: start;
      gap: var(--gap-1);
      padding-bottom: var(--sp-3);
      border-bottom: 1px solid var(--p-surface-200);

      &:nth-last-child(1 of .loadboard-option) {
        padding-bottom: 0;
        margin-bottom: var(--sp-3);
      }

      .p-checkbox {
        width: var(--sp-7);
        height: var(--sp-7);

        .p-checkbox-box {
          width: 100%;
          height: 100%;
        }
      }

      label {
        margin: 0;

        .dat-logo {
          height: var(--sp-4);
          width: auto;
        }

        .truckstop-logo {
          height: var(--sp-12);
          width: auto;
        }
      }
    }
  }
}
</style>
